import * as Sentry from '@sentry/react';
import 'bootstrap/dist/css/bootstrap.min.css';
import * as dotenv from 'dotenv';
import React from 'react';
import ReactDOM from 'react-dom';
import 'react-perfect-scrollbar/dist/css/styles.css';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import * as z from 'zod';
import packageInfo from '../package.json';
import MainConfigurations from './components/MainConfigurations/MainConfigurations';
import './index.scss';
import store from './redux/config/configureStore';
import * as serviceWorker from './serviceWorker';
dotenv.config();

//intiate the sentry logging only when in stage or production
if (process.env.REACT_APP_ENV !== 'development') {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [
      new Sentry.BrowserTracing({
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [
          'localhost',
          /^https:\/\/achieved\.io/,
          /^https:\/\/achieved-stg\.io/,
        ],
      }),
      new Sentry.Replay({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    release: packageInfo.version,
    // the environment
    environment: process.env.REACT_APP_ENV,
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions

    replaysSessionSampleRate: 0.1, // Capture Replay for 10% of all sessions,
    replaysOnErrorSampleRate: 1.0, // plus for 100% of sessions with an error
  });

  // Override console.log
  const originalConsoleLog = console.log;
  console.log = (...args: any[]) => {
    // Only send logs in production
    if (process.env.REACT_APP_ENV !== 'development') {
      Sentry.setExtra('log', args);
      Sentry.captureMessage(args.join(' '));
    }
    // in development mode, print the message into the console
    else originalConsoleLog(...args);
  };

  // Override console.error
  const originalConsoleError = console.error;
  console.error = (...args: any[]) => {
    // Only send errors in production
    if (process.env.REACT_APP_ENV !== 'development') {
      Sentry.setExtra('error', args);
      Sentry.captureException(new Error(args.join(' ')));
    } else originalConsoleError(...args);
  };
}

const customErrorMap: z.ZodErrorMap = (issue, ctx) => {
  if (issue.code === 'invalid_type') {
    if (issue.received === 'null' || issue.received === 'undefined') {
      return { message: 'Required' };
    }
  }
  if (issue.code === 'invalid_enum_value') {
    return { message: 'Required' };
  }
  return { message: ctx.defaultError };
};

z.setErrorMap(customErrorMap);

function renderMainConfigurations() {
  return <MainConfigurations />;
}

/**
 * Ensures that the URL starts with /dashboard, otherwise redirects to the correct path.
 */
window.onload = function () {
  // Get the current path from the URL
  const currentPath = window.location.pathname;

  // If the current path does not start with /dashboard and it is not the root path
  if (!currentPath.startsWith('/dashboard') && currentPath !== '/') {
    // Construct the new URL with /dashboard prefix
    const newUrl = '/dashboard' + currentPath + window.location.search + window.location.hash;

    // Use history.pushState to update the URL without causing a full page reload
    window.history.pushState({ path: newUrl }, '', newUrl);
  }
};

ReactDOM.render(
  <React.StrictMode>
    <Provider store={store}>
      <BrowserRouter>{renderMainConfigurations()}</BrowserRouter>
    </Provider>
  </React.StrictMode>,
  document.getElementById('root'),
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
